// changeLogUtils.js
// ---------------------------------------------------------------------------
// Pure helpers for the Survey Builder change log.
//
// A "change" is captured as a list of field-level `ops`. Each op targets a
// shallow path inside sdataGroup so that array index-shifts can never corrupt
// it and conflict detection is a simple path-equality check:
//
//   op = { path: [questionKey]            , before, after }   // whole question add/delete
//   op = { path: [questionKey, property]  , before, after }   // one property changed
//
// `before`/`after` hold the WHOLE value at that path (scalars, or entire
// rows/columns/statements arrays), matching how the edit handlers replace
// whole arrays. A value of `undefined` means "absent" (added or deleted).
// ---------------------------------------------------------------------------

const clone = (v) =>
  v === undefined ? undefined : JSON.parse(JSON.stringify(v));

const valueEquals = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const pathEquals = (a = [], b = []) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const pathKey = (path) => JSON.stringify(path);

export const getAtPath = (obj, path) => {
  let cur = obj;
  for (const seg of path) {
    if (cur == null) return undefined;
    cur = cur[seg];
  }
  return cur;
};

export const isPlainObject = (v) =>
  v != null && typeof v === "object" && !Array.isArray(v);

// Immutably set (or delete, when value === undefined) an arbitrary-depth path.
export const setAtPath = (root, path, value) => {
  const [head, ...rest] = path;
  const base = isPlainObject(root) ? { ...root } : {};
  if (rest.length === 0) {
    if (value === undefined) delete base[head];
    else base[head] = clone(value);
    return base;
  }
  base[head] = setAtPath(base[head], rest, value);
  return base;
};

/**
 * Diff two data groups. Granularity:
 *   - whole question add/delete       → path [questionKey]
 *   - scalar / array property change  → path [questionKey, property]
 *   - object property (e.g. oeparameters): descend one level so each sub-key is
 *     independently revertible          → path [questionKey, property, subKey]
 *
 * Sub-key granularity for object fields is what lets "later wins" work per
 * setting (e.g. reverting `textarea` while keeping a later `optional` change).
 */
export const diffDataGroups = (prev = {}, next = {}) => {
  const ops = [];
  const prevObj = prev || {};
  const nextObj = next || {};
  const keys = new Set([...Object.keys(prevObj), ...Object.keys(nextObj)]);

  keys.forEach((key) => {
    const pq = prevObj[key];
    const nq = nextObj[key];

    if (pq === undefined && nq !== undefined) {
      ops.push({ path: [key], before: undefined, after: clone(nq) });
      return;
    }
    if (pq !== undefined && nq === undefined) {
      ops.push({ path: [key], before: clone(pq), after: undefined });
      return;
    }
    if (pq === nq) return;

    // Both present — compare each property.
    const props = new Set([
      ...Object.keys(pq || {}),
      ...Object.keys(nq || {}),
    ]);
    props.forEach((prop) => {
      const pv = pq ? pq[prop] : undefined;
      const nv = nq ? nq[prop] : undefined;
      if (valueEquals(pv, nv)) return;

      // Object-valued property present on both sides → descend to sub-keys.
      if (isPlainObject(pv) && isPlainObject(nv)) {
        const subKeys = new Set([...Object.keys(pv), ...Object.keys(nv)]);
        subKeys.forEach((sk) => {
          if (!valueEquals(pv[sk], nv[sk])) {
            ops.push({
              path: [key, prop, sk],
              before: clone(pv[sk]),
              after: clone(nv[sk]),
            });
          }
        });
        return;
      }

      ops.push({ path: [key, prop], before: clone(pv), after: clone(nv) });
    });
  });

  return ops;
};

const questionName = (group, key) => {
  const q = group && group[key];
  return (q && (q.Question_number || q["Question number"])) || key;
};

const arrLen = (v) => (Array.isArray(v) ? v.length : 0);

const describeOp = (op, prev, next) => {
  const [key, prop, subKey] = op.path;

  if (prop === undefined) {
    if (op.before === undefined) return `Added ${questionName(next, key)}`;
    return `Deleted ${questionName(prev, key)}`;
  }

  const name = questionName(op.after !== undefined ? next : prev, key);

  // Sub-key of an object property (e.g. oeparameters.textarea).
  if (subKey !== undefined) {
    return `Edited ${LABELS[prop] || prop} · ${subKey} of ${name}`;
  }

  switch (prop) {
    case "Qtitle":
      return `Edited title of ${name}`;
    case "instructions":
      return `Edited instructions of ${name}`;
    case "type":
      return `Changed type of ${name}${op.after ? ` to ${op.after}` : ""}`;
    case "_order":
      return `Reordered ${name}`;
    case "rows":
    case "columns":
    case "statements":
      return `Updated ${LABELS[prop] || prop} of ${name} (${arrLen(
        op.before
      )}→${arrLen(op.after)})`;
    default:
      return `Edited ${LABELS[prop] || prop} of ${name}`;
  }
};

/**
 * Build a short human description for a set of ops.
 */
export const describeOps = (ops = [], prev = {}, next = {}) => {
  if (!ops.length) return "No changes";
  if (ops.length === 1) return describeOp(ops[0], prev, next);

  // Pure reorder (drag/drop or bulk move) touches only _order on many questions.
  if (ops.every((o) => o.path.length === 2 && o.path[1] === "_order")) {
    return `Reordered ${ops.length} questions`;
  }

  const first = describeOp(ops[0], prev, next);
  const rest = ops.length - 1;
  return `${first} (+${rest} more change${rest > 1 ? "s" : ""})`;
};

/**
 * Compute the new data group when reverting `target`, keeping later edits.
 *
 * "Later wins": for each op in the target entry, restore the field to its
 * pre-target value ONLY if no later, non-reverted entry touched that same path.
 *
 * @param current       the live data group
 * @param target        the entry being reverted ({ ops: [...] })
 * @param laterEntries  entries chronologically AFTER target ({ ops, reverted })
 */
// ── Display helpers: turn ops into a human-readable before/after diff ───────

export const stripHtml = (v) =>
  typeof v === "string"
    ? v
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    : v;

const truncate = (s, n = 240) =>
  typeof s === "string" && s.length > n ? `${s.slice(0, n)}…` : s;

const itemText = (it) => {
  if (it == null) return "";
  const t = stripHtml(it.text ?? it.label ?? "");
  return t || (it.index != null ? `#${it.index}` : "");
};

// Item-level diff for rows / columns / statements arrays.
export const diffItems = (before = [], after = []) => {
  const all = [...before, ...after];
  const useIndex = all.length > 0 && all.every((it) => it && it.index != null);
  const keyOf = (it, i) => (useIndex ? String(it.index) : `pos_${i}`);

  const bMap = new Map();
  before.forEach((it, i) => bMap.set(keyOf(it, i), it));
  const aMap = new Map();
  after.forEach((it, i) => aMap.set(keyOf(it, i), it));

  const added = [];
  const removed = [];
  const changed = [];
  aMap.forEach((it, k) => {
    if (!bMap.has(k)) {
      added.push(itemText(it));
    } else {
      const bt = itemText(bMap.get(k));
      const at = itemText(it);
      if (bt !== at) changed.push({ before: bt, after: at });
    }
  });
  bMap.forEach((it, k) => {
    if (!aMap.has(k)) removed.push(itemText(it));
  });
  return { added, removed, changed };
};

const LABELS = {
  Qtitle: "Title",
  instructions: "Instructions",
  type: "Type",
  _order: "Order",
  rows: "Rows",
  columns: "Columns",
  statements: "Statements",
  oeparameters: "OE settings",
  oeParameters: "OE settings",
};

// Render any value as a short human string.
const displayValue = (v) => {
  if (v === undefined || v === null || v === "") return "";
  if (typeof v === "string") return truncate(stripHtml(v));
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return truncate(JSON.stringify(v), 120);
  } catch {
    return String(v);
  }
};

// Key-by-key diff of two plain objects (e.g. oeparameters).
const diffObjects = (before = {}, after = {}) => {
  const keys = new Set([
    ...Object.keys(before || {}),
    ...Object.keys(after || {}),
  ]);
  const fields = [];
  keys.forEach((k) => {
    const bv = before ? before[k] : undefined;
    const av = after ? after[k] : undefined;
    if (JSON.stringify(bv) !== JSON.stringify(av)) {
      fields.push({ key: k, before: displayValue(bv), after: displayValue(av) });
    }
  });
  return fields;
};

/**
 * Turn a single op into a display object the UI can render:
 *   { label, kind: 'scalar'|'array'|'question-add'|'question-del',
 *     before, after, added, removed, changed }
 */
export const formatOp = (op) => {
  const [key, prop, subKey] = op.path;

  if (prop === undefined) {
    const q = op.after !== undefined ? op.after : op.before;
    const summary = `${(q && (q.Question_number || key)) || key}${
      q && q.Qtitle ? ` — ${truncate(stripHtml(q.Qtitle), 120)}` : ""
    }`;
    return {
      label: "Question",
      kind: op.after !== undefined ? "question-add" : "question-del",
      before: summary,
      after: summary,
    };
  }

  const baseLabel = LABELS[prop] || prop;

  // Sub-key of an object property (e.g. oeparameters.textarea).
  if (subKey !== undefined) {
    return {
      label: `${baseLabel} · ${subKey}`,
      kind: "scalar",
      before: displayValue(op.before),
      after: displayValue(op.after),
    };
  }

  if (prop === "rows" || prop === "columns" || prop === "statements") {
    return {
      label: baseLabel,
      kind: "array",
      ...diffItems(op.before || [], op.after || []),
    };
  }

  // Whole object property appeared/disappeared — show sub-keys that changed.
  if (isPlainObject(op.before) || isPlainObject(op.after)) {
    return {
      label: baseLabel,
      kind: "object",
      fields: diffObjects(op.before || {}, op.after || {}),
    };
  }

  return {
    label: baseLabel,
    kind: "scalar",
    before: displayValue(op.before),
    after: displayValue(op.after),
  };
};

export const applyRevert = (current = {}, target, laterEntries = []) => {
  const laterPaths = new Set();
  laterEntries.forEach((entry) => {
    if (!entry || entry.reverted) return;
    (entry.ops || []).forEach((op) => laterPaths.add(pathKey(op.path)));
  });

  let next = { ...current };
  (target.ops || []).forEach((op) => {
    if (!laterPaths.has(pathKey(op.path))) {
      next = setAtPath(next, op.path, op.before);
    }
    // else: a later change owns this field — leave it (merge with later change)
  });

  return next;
};
